import React from 'react';
import Button from '@paperstac/ui/lib/Button';
import Box from '@paperstac/ui/lib/Box';
import useFirestoreSubscribe from '@paperstac/common/lib/hooks/useFirestoreSubscribe';
import { firestore } from '../../../services/firebaseClient';
import LoadingMessage from '../../common/LoadingMessage';
import SelectServicerLoansSpreadsheet from './SelectServicerLoansSpreadsheet';
import uniq from 'lodash/uniq';
import LinkButton from '@paperstac/ui/lib/LinkButton';
import useImportServicerLoansAsAccountNotes from './useImportServicerLoansAsAccountNotes';
import toast from '@paperstac/ui/lib/Toast';
import { AccountsContext } from '@paperstac/common/lib/components/AccountsProvider';

const SelectServicerLoans = React.memo(({ integrationId, setShowImporter }) => {
  const { currentAccountId } = React.useContext(AccountsContext);
  const [servicerLoans, setServicerLoans] = React.useState(null);
  const [selectedLoanIds, setSelectedLoanIds] = React.useState([]);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [allServicerLoans] = useFirestoreSubscribe(
    firestore.collection('servicerLoans').where('integrationId', '==', integrationId),
    []
  );
  const importServicerLoansAsAccountNotes = useImportServicerLoansAsAccountNotes(currentAccountId);

  const showLoading = !servicerLoans;
  const hasLoans = !showLoading && !!servicerLoans.length;
  const showImportButton = !showLoading && !!selectedLoanIds.length;

  const handleSubmit = React.useCallback(async () => {
    if (isSubmitting) return;
    try {
      setSubmitting(true);
      await importServicerLoansAsAccountNotes(selectedLoanIds);
      toast.success('Import successful');
      setShowImporter(false);
    } catch (e) {
      toast.error(e.message);
      setSubmitting(false);
    }
  }, [importServicerLoansAsAccountNotes, isSubmitting, selectedLoanIds, setShowImporter]);

  const toggleLoanId = React.useCallback(
    (loanId) => {
      selectedLoanIds.includes(loanId)
        ? setSelectedLoanIds(selectedLoanIds.filter((id) => loanId !== id))
        : setSelectedLoanIds(uniq([...selectedLoanIds, loanId]));
    },
    [selectedLoanIds]
  );

  const selectAll = React.useCallback(() => {
    setSelectedLoanIds(servicerLoans.map((sl) => sl.id));
  }, [servicerLoans]);

  const deselectAll = React.useCallback(() => {
    setSelectedLoanIds([]);
  }, []);

  React.useEffect(() => {
    if (!allServicerLoans) return;

    setServicerLoans(
      allServicerLoans.filter((sl) => {
        return !sl.linkedResources.reduce((hasAccountNote, linkedResource) => {
          return hasAccountNote || linkedResource.type === 'AccountNote';
        }, false);
      })
    );
  }, [allServicerLoans]);

  if (showLoading) return <LoadingMessage />;

  return (
    <>
      <Box fontSize={3} mb={5}>
        {hasLoans ? 'Choose which loans to import...' : 'You do not have any loans available for import.'}
      </Box>
      {servicerLoans.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <LinkButton onClick={selectAll}>Select all {servicerLoans.length} loans</LinkButton>
          {!!selectedLoanIds.length && (
            <>
              {' | '}
              <LinkButton onClick={deselectAll}>Deselect all loans</LinkButton>
            </>
          )}
        </Box>
      )}
      <SelectServicerLoansSpreadsheet
        selectedLoanIds={selectedLoanIds}
        servicerLoans={servicerLoans}
        toggleLoanId={toggleLoanId}
      />
      <Box my={5}>
        {showImportButton && (
          <Button variant="primary" onClick={handleSubmit} busy={isSubmitting}>
            {selectedLoanIds.length === 1 ? 'Import Loan' : `Import ${selectedLoanIds.length} Loans`}
          </Button>
        )}{' '}
        <Button onClick={() => setShowImporter(false)}>Cancel</Button>
      </Box>
    </>
  );
});

SelectServicerLoans.propTypes = {};

SelectServicerLoans.defaultProps = {};

SelectServicerLoans.displayName = 'SelectServicerLoans';

export default SelectServicerLoans;
